import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
  RiCornerDownLeftLine,
  RiLightbulbLine,
  RiLoader4Line,
  RiSearch2Line,
  RiSearchLine,
  RiSparkling2Line,
} from '@remixicon/react';
import { getStatusLabel } from '@shared/status-labels';
import { useRouter } from '@tanstack/react-router';
import { motion, type Transition } from 'motion/react';
import { Children, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { cn } from 'src/components/ui/utils';
import { navigateHref } from 'src/lib/routing';
import { getCollectionConfig } from '../constants/collections';
import { getEntitledSections, getSectionConfig, sectionDiscriminatorDoc } from '../constants/sections';
import { type CommandAction, matchesCommand, useCommandActions } from '../hooks/useCommandActions';
import {
  type SearchResultGroup,
  type SearchResultItem,
  type StatusFacet,
  useGlobalSearch,
} from '../hooks/useGlobalSearch';
import { useRecentRecords } from '../hooks/useRecentRecords';
import type { Filter } from '../utils/filters';
import {
  GS_KBD_CLASS,
  getCaretOffset,
  isCaretAtPillEnd,
  MONO,
  QUERY_BOUNDARY,
  renderField,
  sanitizePastedText,
  setCaretOffset,
} from '../utils/queryField';
import { parseQuerySpans, scopeExamples } from '../utils/scopeQuery';
import { resolveAccent } from './palette/accents';
import { type PaletteCardData, ResultCard } from './palette/ResultCard';
import { SEARCH_BAR_LAYOUT_ID, SEARCH_ICON_LAYOUT_ID } from './searchBarMotion';

/**
 * ⌘K command palette — the Typesense-backed global search surface. The raw query
 * STRING is the single source of truth;
 * TWO orthogonal narrowing mechanisms derive from it plus the view state:
 *
 *   FILTER PILLS (breadth) ─ a registered `so:` / `is:open` token typed ANYWHERE in
 *     the text. The text is re-parsed every keystroke into `{start,end,filter}`
 *     spans; the field is ONE contenteditable layer whose token spans (plus the value
 *     typed into them) render as Kbd chips — the caret is native to the very text that
 *     carries the chips, so a chip can be entered, extended, and escaped like any run
 *     of characters. Editing a token re-parses live — backspace the `:` out of `so:`
 *     and the chip dissolves back to plain text. Narrows WHICH sections appear.
 *   SCOPE (depth) ─ one section. Entered ONLY by "Show all N". The scoped view:
 *     single section, 20/page, status sub-facets. Filter pills STAY APPLIED inside.
 *
 *   ALL view ─┬─ empty query ─▶ recents
 *             └─ typing ─▶ type facets · inline pills (in the text) · one Best match
 *                          · per-group "Show all N" ─┐
 *                                                    ▼
 *   SCOPED view ─ one section · status sub-facets · flat list · pills still apply
 *              └── esc ─▶ strips the token pills + exits scope (keeps the term text)
 *
 * Key map:  ↑↓   move one highlight across the flat list (result-row cursor)
 *           ↵    open the highlight
 *           Tab  cycle the visible chip row's chips (filter chips when unscoped,
 *                status sub-facets when scoped) — Shift+Tab reverses, wrapping
 *                through null (the result list). The cursored chip scrolls into view.
 *           ←→   NATIVE text caret — moves through a chip character by character; Left
 *                re-enters the preceding chip, Right at a chip's end escapes it (a SPACE
 *                stays inside the chip now — only Right escapes, via a boundary marker)
 *           ⌫    NATIVE character delete (re-parse dissolves a broken token); at caret
 *                offset 0 it clears the TOP-LEVEL type facet instead
 *           esc  strip token pills + exit scope, else close
 *
 * The result-row cursor (`activeIndex`, ↑/↓) and the facet cursor (`facetIndex`,
 * Tab) are VIRTUAL — they drive `aria-activedescendant` while real DOM focus stays
 * on the input; the text caret (←/→/⌫) is separate and fully native. Reads
 * exclusively through the scoped `searchKey` ⇒ it can surface nothing the caller
 * isn't entitled to. No key ⇒ an "unavailable" state (never Algolia).
 */

// The field's typography — one editable contenteditable layer (no mirror to align to).
// text-20 / 28px line seats the 20px Kbd chips (align-middle) against the running text;
// 28px still fits the shrink-0 60px field row comfortably.
const GS_FIELD_TYPE = 'text-20 leading-[28px] tracking-normal';

// Stable ids for the virtual-cursor combobox: the input's `aria-activedescendant`
// points at one of these so screen readers announce the highlighted row / facet /
// inline chip without real DOM focus ever leaving the input.
const LISTBOX_ID = 'gs-palette-listbox';
const rowOptionId = (index: number) => `gs-palette-opt-${index}`;
const facetOptionId = (index: number) => `gs-palette-facet-${index}`;

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Scoped search key from the search credentials; null while unavailable/loading. */
  searchKey: string | null;
  /** Orders-domain key from the search credentials; null when the member may search no order type. */
  ordersKey?: string | null;
  /** Typesense browser node URL(s) the keys are valid against; null while unavailable/loading. */
  endpoint: string | null;
  /** Entitled collection aliases from the token. */
  collections: string[];
  /** True when no key could mint (Typesense unreachable / no entitlement); the
   * palette opens into an "unavailable" state instead of driving search. */
  unavailable?: boolean;
  /** First scoped-key mint in flight; the palette shows "Searching…", not empty. */
  minting?: boolean;
  /** Query text is lifted to the trigger so it persists across open/close in both
   * directions; the palette reads/writes it through these props. */
  query: string;
  onQueryChange: (value: string) => void;
  /** Shared-layout spring the trigger also applies to the header bar, so the
   * `layoutId` morph reads identically whichever side initiates it. Reduced-motion
   * collapses it to an instant cut. */
  morphTransition?: Transition;
}

type NavEntry = { kind: 'command'; action: CommandAction } | { kind: 'result'; item: PaletteCardData };

// One cursorable chip in the (single, view-dependent) chip row. Carries its own
// selected state + activation so the render, the Tab/Enter handlers, and the
// scroll-into-view all read one list regardless of which row is showing.
type ChipModel = { key: string; label: string; count: number; selected: boolean; onSelect: () => void };

const pluralize = (label: string) => `${label}s`;

export function CommandPalette({
  open,
  onClose,
  searchKey,
  ordersKey,
  endpoint,
  collections,
  unavailable = false,
  minting = false,
  query,
  onQueryChange,
  morphTransition,
}: CommandPaletteProps) {
  const router = useRouter();
  const setQuery = onQueryChange;
  // Precedence for leg selection: scope > typeFacet > typed type pills.
  const [scope, setScope] = useState<string | null>(null);
  const [statusValue, setStatusValue] = useState<string | null>(null);
  const [typeFacet, setTypeFacet] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Facet-row cursor (null = list has focus); Tab moves it.
  const [facetIndex, setFacetIndex] = useState<number | null>(null);
  // The contenteditable query field. Its children are built imperatively (renderField),
  // never by React — so a re-render from a settling search can't reconcile the DOM back
  // and wipe an in-flight IME composition.
  const fieldRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  // True while an IME composition is in flight; keydown routing must not hijack
  // Enter/Escape/Arrows (now incl. Right, which escapes a chip) mid-compose, and the
  // rebuild effect must not touch the DOM the composer owns.
  const isComposingRef = useRef(false);
  // The string offset to seat the caret at after the next field rebuild. Set by every
  // path that mutates the query (a keystroke, a facet tap, Esc, a Right-arrow escape);
  // consumed and cleared by the rebuild effect. null ⇒ keep the live caret in place.
  const caretTargetRef = useRef<number | null>(null);

  const scoped = scope !== null;

  // Reset transient view state each open so the palette never reopens onto a stale
  // view. Query text is deliberately NOT cleared — it lives in the trigger and must
  // persist across open/close in both directions.
  useEffect(() => {
    if (open) {
      setScope(null);
      setStatusValue(null);
      setTypeFacet(null);
      setActiveIndex(0);
      setFacetIndex(null);
    }
  }, [open]);

  // Single source of truth: re-parse the raw query every render into token spans
  // (painted as pills), the derived breadth filters, and the bare search term.
  const parse = useMemo(() => parseQuerySpans(query, collections), [query, collections]);
  const spans = parse.spans;
  const term = parse.term;
  const trimmed = term;

  // Rebuild the field's DOM from the query (chips + plain text) and restore the caret.
  const typeOverridden = typeFacet !== null;
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!field || isComposingRef.current) return;
    renderField(field, query, spans, caretTargetRef.current, typeOverridden);
    caretTargetRef.current = null;
  }, [open, query, spans, typeOverridden]);

  // Declared AFTER the rebuild effect so the field's nodes exist before the caret is seated; this overrides react-aria's autofocus, which would land the caret at the start.
  useLayoutEffect(() => {
    if (!open) return;
    const field = fieldRef.current;
    if (!field) return;
    field.focus();
    setCaretOffset(field, query.length);
    // Caret placement is an open-time concern only; re-running on every keystroke would
    // fight the rebuild effect's own restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // The filters handed to the search: derived type + lifecycle, plus the scoped
  // status sub-facet (the one filter that isn't in the text).
  const filters = useMemo<Filter[]>(
    () => (statusValue !== null ? [...parse.filters, { dim: 'status', value: statusValue }] : parse.filters),
    [parse.filters, statusValue]
  );

  const allCommands = useCommandActions();
  const commands = useMemo(
    () => (scoped ? [] : allCommands.filter((action) => matchesCommand(action, term))),
    [allCommands, term, scoped]
  );
  const { groups, totalFound, statusFacets, loading, settled } = useGlobalSearch({
    searchKey,
    ordersKey,
    endpoint,
    collections,
    query: term,
    scope,
    filters,
    // AUTHORITATIVE: when the top-level facet is set it governs breadth over the typed
    // `type` pills — resolveQuerySections uses it and ignores those pills.
    typeFacet,
    // The header bar is typeable while the palette is closed; gate on `open` so a
    // keystroke there never fires a request with nothing on screen.
    enabled: open,
  });
  const { recents, record } = useRecentRecords(collections);

  // The single-select scoped status sub-facet value, or null (drives the buckets cache).
  const statusFilter = statusValue;

  // Typesense collapses `facet_counts` under `filter_by`, so cache the last
  // unfiltered buckets — else a status filter zeroes the others.
  const [stableStatusFacets, setStableStatusFacets] = useState<StatusFacet[]>([]);
  useEffect(() => {
    if (!scoped) setStableStatusFacets([]);
    else if (statusFilter === null && !loading) setStableStatusFacets(statusFacets);
  }, [scoped, statusFilter, loading, statusFacets]);

  const hasTypeFilter = useMemo(() => filters.some((f) => f.dim === 'type'), [filters]);

  // Chips come from the last unscoped, unfiltered search — a superseded snapshot is ignored.
  const [facetSnapshot, setFacetSnapshot] = useState<{
    query: string;
    chips: { key: string; label: string; count: number }[];
  }>({ query: '', chips: [] });
  useEffect(() => {
    if (!scoped && !hasTypeFilter && typeFacet === null) {
      setFacetSnapshot({
        query: trimmed,
        chips: groups.map((group) => ({
          key: group.section.key,
          label: pluralize(group.section.label),
          count: group.found,
        })),
      });
    }
  }, [groups, scoped, hasTypeFilter, typeFacet, trimmed]);

  const facets = useMemo(() => {
    const chips = facetSnapshot.query === trimmed ? facetSnapshot.chips : [];
    return [{ key: null as string | null, label: 'All', count: totalFound }, ...chips];
  }, [facetSnapshot, trimmed, totalFound]);

  // Entitled section keys, so a facet tap / typed token defensively never adds a
  // type filter for a section the caller's key does not grant.
  const entitledSectionKeys = useMemo(
    () => new Set(getEntitledSections(collections).map((section) => section.key)),
    [collections]
  );

  // Single cross-collection Best match: the highest raw `text_match` in the all view.
  // CAVEAT: each collection is queried with its own `query_by` ladder (no `query_by_weights`/`text_match_type`), so scores are not strictly comparable across collections. If it mis-ranks the default ↵ target, the fix is explicit per-collection `query_by_weights` or a per-collection top-hit rule (TAS-3542).
  const bestMatch = useMemo(() => {
    if (scoped || trimmed.length === 0) return null;
    const all = groups.flatMap((group) => group.items);
    return all.reduce<SearchResultItem | null>(
      (best, item) => (best === null || item.textMatch > best.textMatch ? item : best),
      null
    );
  }, [groups, scoped, trimmed]);

  // De-dup the promoted Best match out of its own group, scoped to (collection, id) — Typesense ids are per-collection primary keys, so a lot and an order can share a bare id and a plain `id` compare would drop the wrong row.
  const renderGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => item.collectionKey !== bestMatch?.collectionKey || item.id !== bestMatch?.id
          ),
        }))
        .filter((group) => group.items.length > 0),
    [groups, bestMatch]
  );

  // Keep the highlighted row inside the scrolling body — holding ↓ across many
  // entitled collections must not walk the selection off-screen.
  useEffect(() => {
    listboxRef.current
      ?.querySelector<HTMLElement>(`#${rowOptionId(activeIndex)}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Match the scoped group by identity, not slot 0: `groups` isn't cleared when
  // the scope changes, so a positional read renders the previous view's first
  // group during the in-flight window. Undefined here routes into the skeleton.
  const scopedGroup = scope ? groups.find((group) => group.key === scope) : undefined;

  // The flat highlight order for ↑/↓ + ↵.
  const navEntries = useMemo<NavEntry[]>(() => {
    if (scoped) return (scopedGroup?.items ?? []).map((item) => ({ kind: 'result' as const, item }));
    // Empty query: recents lead, then the quick create-actions. Traversal order
    // must mirror the on-screen order (recents block, then actions block).
    if (trimmed.length === 0)
      return [
        ...recents.map((item) => ({ kind: 'result' as const, item })),
        ...commands.map((action) => ({ kind: 'command' as const, action })),
      ];
    return [
      ...commands.map((action) => ({ kind: 'command' as const, action })),
      ...(bestMatch ? [{ kind: 'result' as const, item: bestMatch }] : []),
      ...renderGroups.flatMap((group) => group.items.map((item) => ({ kind: 'result' as const, item }))),
    ];
  }, [scoped, trimmed, recents, commands, bestMatch, renderGroups, scopedGroup]);

  useEffect(() => {
    setActiveIndex((index) => Math.min(index, Math.max(navEntries.length - 1, 0)));
  }, [navEntries.length]);

  // Set the query text and seat the caret at its end after the rebuild — used by facet
  // taps, Esc, and any path that mutates the string programmatically while focus stays
  // on the field. The rebuild effect consumes `caretTargetRef`, so no rAF is needed.
  const setQueryCaretEnd = (next: string) => {
    caretTargetRef.current = next.length;
    setQuery(next);
  };

  const clearQuery = () => {
    setFacetIndex(null);
    setActiveIndex(0);
    setQueryCaretEnd('');
  };

  // Read the field's live text after an edit and push it into the query state (the
  // single source of truth). Snapshots the caret from the DOM the browser just edited so
  // the rebuild reseats it exactly. Shared by onInput and compositionend.
  const commitFieldValue = () => {
    const field = fieldRef.current;
    if (!field) return;
    // contenteditable can materialise a preserved space as U+00A0; normalise so token
    // parsing and the search term never carry a non-breaking space. Same length ⇒ the
    // caret offset stays valid.
    const text = (field.textContent ?? '').replace(/\u00A0/g, ' ');
    caretTargetRef.current = getCaretOffset(field) ?? text.length;
    setQuery(text);
    setActiveIndex(0);
    setFacetIndex(null);
    // A status picked for the previous query must not survive editing (it would freeze
    // the cached buckets).
    setStatusValue(null);
  };

  const enterScope = (key: string) => {
    setScope(key);
    setStatusValue(null);
    setFacetIndex(null);
    setActiveIndex(0);
  };
  // Leave only the depth scope (the scoped-view back affordance keeps breadth pills).
  const exitScope = () => {
    setScope(null);
    setStatusValue(null);
    setFacetIndex(null);
    setActiveIndex(0);
  };
  const toggleTypeFacet = (key: string | null) => {
    setFacetIndex(null);
    setActiveIndex(0);
    if (key === null || key === typeFacet) {
      setTypeFacet(null);
      return;
    }
    if (!entitledSectionKeys.has(key)) return;
    setTypeFacet(key);
  };

  const activate = (entry: NavEntry | undefined) => {
    if (!entry) return;
    if (entry.kind === 'command') {
      entry.action.run();
    } else {
      record(entry.item);
      void navigateHref(router, entry.item.route);
    }
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // IME safety: while composing, every key belongs to the composer — never route
    // Enter/Escape/Arrows to the palette (they'd commit/cancel the composition).
    if (event.nativeEvent.isComposing || isComposingRef.current) return;
    switch (event.key) {
      case 'Escape':
        if (scoped || statusValue !== null || spans.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
          setScope(null);
          setStatusValue(null);
          setFacetIndex(null);
          setActiveIndex(0);
          setQueryCaretEnd(term); // `term` is the query with all token spans removed.
        } else {
          onClose();
        }
        return;
      case 'Tab':
        if (chipRowVisible) {
          event.preventDefault();
          setFacetIndex((index) => {
            if (event.shiftKey) {
              if (index === null) return cursorChips.length - 1;
              return index === 0 ? null : index - 1;
            }
            const next = index === null ? 0 : index + 1;
            return next >= cursorChips.length ? null : next;
          });
        }
        return;
      case 'ArrowRight': {
        // Right-arrow at a chip's END escapes it: a space is chip content now, so the boundary needs an explicit zero-width marker (QUERY_BOUNDARY) with the caret dropped past it, and the next keystroke re-parses OUTSIDE the chip.
        // The marker lives in the string, so it relocates naturally under edits, stops the paint run in pillTokenEnd, and is stripped before the term reaches Typesense. Inside the chip, or with a marker/delimiter already after it, the caret moves natively.
        const field = fieldRef.current;
        const selection = window.getSelection();
        if (!field || !selection || !selection.isCollapsed) return; // let a selection collapse natively
        const caret = getCaretOffset(field);
        if (caret === null) return;
        if (!isCaretAtPillEnd(query, spans, caret) || /[\s\u200b]/.test(query[caret] ?? '')) return;
        event.preventDefault();
        caretTargetRef.current = caret + 1;
        setQuery(`${query.slice(0, caret)}${QUERY_BOUNDARY}${query.slice(caret)}`);
        return;
      }
      case 'Backspace': {
        // ⌫ at offset 0 clears the type facet; everywhere else it is a native delete.
        if (typeFacet === null) return;
        const field = fieldRef.current;
        const selection = window.getSelection();
        if (!field || !selection || !selection.isCollapsed) return; // let a real selection delete natively
        if (getCaretOffset(field) !== 0) return; // only the top-level clear at offset 0
        event.preventDefault();
        setTypeFacet(null);
        return;
      }
      case 'ArrowDown':
        event.preventDefault();
        if (facetIndex !== null) setFacetIndex(null);
        else setActiveIndex((index) => Math.min(index + 1, navEntries.length - 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        // Symmetric with ArrowDown: clear the facet cursor before moving the list.
        if (facetIndex !== null) setFacetIndex(null);
        else setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      case 'Enter': {
        event.preventDefault();
        if (facetIndex !== null) {
          // Guard the access — `facetIndex` isn't clamped synchronously, so a
          // shrunk `cursorChips` (e.g. a rejected search clears groups) can leave it
          // pointing past the end.
          const chip = cursorChips[facetIndex];
          if (!chip) {
            setFacetIndex(null);
            return;
          }
          chip.onSelect();
          return;
        }
        activate(navEntries[activeIndex]);
        return;
      }
    }
  };

  // Flat highlight index handed to each rendered row. INVARIANT: rows must be
  // rendered in the SAME order `navEntries` is built (empty ⇒ recents; else
  // commands then groups) so `nextActive()` and `activeIndex` address one list.
  let cursor = 0;
  const nextActive = () => cursor++;

  // Tab identity comes from the SCOPED COLLECTION, not the result set, so label/icon survive an empty filtered response.
  // `scopedFoundCount` = what THIS search matched (header); `scopedAllStatusCount` = across every status bucket (the "All" chip). Keep them separate.
  const scopedSectionConfig = scope ? getSectionConfig(scope) : undefined;
  const scopedCollectionConfig = scopedSectionConfig ? getCollectionConfig(scopedSectionConfig.collection) : undefined;
  const scopedConfigLabel = scopedSectionConfig ? pluralize(scopedSectionConfig.label) : 'results';
  const scopedFoundCount = scopedGroup?.found ?? 0;
  const scopedAllStatusCount = stableStatusFacets.reduce((sum, facet) => sum + facet.count, 0);
  // Accent from a live hit, else the section's identity via a discriminator-only
  // stub doc — so a PO scope pill is purple even before its first result lands.
  const scopedAccentKey =
    scopedGroup?.items[0]?.accentKey ??
    (scopedCollectionConfig && scopedSectionConfig
      ? scopedCollectionConfig.accentKey(sectionDiscriminatorDoc(scopedSectionConfig))
      : '');
  const scopedAccent = resolveAccent(scopedAccentKey);
  const ScopedIcon = scopedAccent.icon;

  // The two chip rows never coexist, so one cursor (`facetIndex`) walks whichever is visible.
  const cursorChips = useMemo<ChipModel[]>(() => {
    if (scoped) {
      if (stableStatusFacets.length === 0) return [];
      return [
        {
          key: '__all__',
          label: 'All',
          count: scopedAllStatusCount,
          selected: statusValue === null,
          onSelect: () => setStatusValue(null),
        },
        ...stableStatusFacets.map((facet) => ({
          key: facet.value,
          label: getStatusLabel(facet.value),
          count: facet.count,
          selected: statusValue === facet.value,
          onSelect: () => setStatusValue(facet.value),
        })),
      ];
    }
    return facets.map((facet) => ({
      key: facet.key ?? '__all__',
      label: facet.label,
      count: facet.count,
      // Selected reads the TOP-LEVEL facet STATE, not the query text — the row is its
      // own control. "All" is selected when no facet is set.
      selected: facet.key === null ? typeFacet === null : typeFacet === facet.key,
      onSelect: () => toggleTypeFacet(facet.key),
    }));
    // toggleTypeFacet is a stable per-render closure over the same deps; listing the
    // primitive inputs keeps the descriptors fresh without re-wrapping it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, stableStatusFacets, scopedAllStatusCount, statusValue, facets, typeFacet]);

  // ONE predicate for the chip row's existence: Tab, the cursor clamp, and the render
  // all gate on it, so the field can never advertise a chip whose row has unmounted.
  const chipRowVisible = !unavailable && cursorChips.length > 1;

  // Clamp the chip cursor when the visible row shrinks or unmounts (a rejected search
  // clears groups, scope toggles, or it settles to a single chip) so it can never point
  // past the end or at a row that is gone — mirrors the activeIndex clamp.
  useEffect(() => {
    setFacetIndex((index) => (index !== null && (!chipRowVisible || index >= cursorChips.length) ? null : index));
  }, [chipRowVisible, cursorChips.length]);

  const hasResults = navEntries.length > 0;
  // The single element the input points at via aria-activedescendant. Priority: the
  // chip-row cursor, then the highlighted list row. (Token pills are painted text,
  // not options — the input's own value carries them for the screen reader.)
  const activeDescendant = unavailable
    ? undefined
    : facetIndex !== null
      ? facetOptionId(facetIndex)
      : hasResults
        ? rowOptionId(activeIndex)
        : undefined;
  // The chip row lives OUTSIDE the listbox the combobox controls, so the input must
  // OWN its chips for `aria-activedescendant` to reference a valid target (list rows
  // are already covered by aria-controls per the combobox pattern).
  const ownedOptionIds =
    (chipRowVisible ? cursorChips.map((_, index) => facetOptionId(index)).join(' ') : '') || undefined;
  // Skeleton latches on request-settle (`!settled`), NEVER on `groups.length`: it shows only while the FIRST request for the current query stream is in flight, so a cold start shows it once and later keystrokes keep the last settled view. The header spinner + "Searching…" carry the in-flight hint (TAS-3542).
  const showSkeleton = !unavailable && loading && !settled && (scoped ? !hasResults : trimmed.length > 0);
  const footerMode: FooterMode = unavailable
    ? 'default'
    : showSkeleton
      ? 'loading'
      : scoped
        ? 'scoped'
        : trimmed.length > 0 && !loading && !hasResults
          ? 'zero'
          : 'default';
  // Example scope tokens for the zero-results tip, drawn from the entitled
  // collections so we never advertise a scope the user's key would 403.
  const scopeHints = useMemo(() => scopeExamples(collections), [collections]);

  return (
    // The bar is a shared-layout element hosted inside this panel: NEVER put a transform (scale/translate) on the bar's ancestor — it corrupts motion's measure of the morph target. The card chrome fades on its own layer behind the bar, and the overlay animates backdrop TINT rather than opacity.
    <ModalOverlay
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      isDismissable
      className="gs-overlay fixed inset-0 z-[210000] flex items-start justify-center bg-[rgba(17,22,34,0.46)] px-16 pt-[12vh]"
    >
      {({ isExiting }) => (
        <Modal className="relative w-full max-w-[640px] overflow-hidden rounded-[16px] outline-none">
          {/* Card chrome — bg/border/shadow on its OWN layer BEHIND the bar so its
                fade never distorts the shared-layout bar's rect (fade only, no transform). */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 rounded-[16px] border border-neutral-4 bg-white shadow-[0_24px_60px_-12px_rgba(16,22,34,0.5)]',
              isExiting ? 'animate-gs-fade-out' : 'animate-gs-fade-in',
              'motion-reduce:animate-none'
            )}
          />
          <Dialog aria-label="Search" className="relative flex max-h-[70vh] flex-col outline-none">
            {/* ── Input header ── */}
            {/* The palette's input row IS the shared-layout bar: same `layoutId` as
                  the header pill, so `motion` promotes the header bar into this row on
                  open and back on close — one continuous element. Dropped the instant a
                  close begins (isExiting), releasing the layoutId to the header pill for a
                  clean re-morph even while the card is still fading out. */}
            {/* shrink-0: the row is a flex child of the max-h-[70vh] column; without it the
                results list (once it fills the cap) shrinks this header below its h-[60px]
                basis (min-height:auto) — ~60px empty, collapsing toward the "N results" text
                height once typing replaces the taller esc badge. */}
            {!isExiting && (
              <motion.div
                layoutId={SEARCH_BAR_LAYOUT_ID}
                layout
                transition={morphTransition}
                className="flex h-[60px] shrink-0 items-center gap-[12px] border-b border-neutral-4 px-[18px]"
              >
                <motion.span
                  layoutId={SEARCH_ICON_LAYOUT_ID}
                  layout
                  transition={morphTransition}
                  className="flex shrink-0 items-center"
                >
                  {loading ? (
                    <span
                      className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-4 border-t-[#667239]"
                      aria-hidden
                    />
                  ) : (
                    <RiSearchLine className="h-5 w-5 text-neutral-alpha-7" aria-hidden />
                  )}
                </motion.span>
                {/* Scoped-view "Back to all results" pill — depth only, entered by
                    "Show all". Dismissing it runs `exitScope` (keeps breadth chips). */}
                {scoped && (
                  <>
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label="Back to all results"
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={exitScope}
                      className="flex h-[28px] shrink-0 items-center gap-[7px] rounded-[8px] bg-brand-alpha-2 pl-[10px] pr-[8px]"
                    >
                      <ScopedIcon className={cn('h-[14px] w-[14px]', scopedAccent.fg)} aria-hidden />
                      <span className="text-13 font-semibold text-brand-6">{scopedConfigLabel}</span>
                      <RiCloseLine className="h-[15px] w-[15px] text-brand-6/70" aria-hidden />
                    </button>
                    <span className="shrink-0 text-18 text-neutral-alpha-7">›</span>
                  </>
                )}
                {/* Single contenteditable field. The tokens ARE the field text; token
                    spans (plus the value typed into them) render as Kbd chips built
                    imperatively by `renderField` (see the rebuild effect), so the caret
                    is native to the very characters that carry the chips — no second
                    layer to keep aligned. `whitespace-pre` + `overflow-x-auto` give the
                    browser native horizontal scroll-to-caret on overflow. */}
                <div className="relative flex min-w-0 flex-1 items-center">
                  {query.length === 0 && (
                    <span
                      aria-hidden
                      className={cn(
                        GS_FIELD_TYPE,
                        'pointer-events-none absolute inset-y-0 flex items-center whitespace-nowrap text-neutral-alpha-7'
                      )}
                    >
                      Search orders, lots, customers…
                    </span>
                  )}
                  <div
                    ref={fieldRef}
                    data-testid="gs-query-field"
                    role="combobox"
                    aria-label="Search"
                    aria-expanded={!unavailable}
                    aria-controls={LISTBOX_ID}
                    aria-owns={ownedOptionIds}
                    aria-activedescendant={activeDescendant}
                    aria-multiline={false}
                    contentEditable
                    suppressContentEditableWarning
                    spellCheck={false}
                    autoCapitalize="off"
                    // The field's children are owned by `renderField`, never React — do
                    // not pass JSX children here.
                    onInput={() => {
                      // While composing, the browser owns the DOM; wait for
                      // compositionend to read the committed text (else we'd rebuild
                      // mid-compose and drop the composition).
                      if (isComposingRef.current) return;
                      commitFieldValue();
                    }}
                    onKeyDown={handleKeyDown}
                    onPaste={(event) => {
                      // `execCommand('insertText')` is deprecated but is the ONLY insertion that preserves the native contenteditable undo stack — a Selection/Range splice breaks Ctrl-Z here. Strip boundary markers from pasted text so it cannot inject a phantom chip boundary.
                      event.preventDefault();
                      const text = sanitizePastedText(event.clipboardData.getData('text/plain'));
                      document.execCommand('insertText', false, text);
                    }}
                    onCompositionStart={() => {
                      isComposingRef.current = true;
                    }}
                    onCompositionEnd={() => {
                      isComposingRef.current = false;
                      commitFieldValue();
                    }}
                    className={cn(
                      GS_FIELD_TYPE,
                      'min-w-0 flex-1 overflow-x-auto whitespace-pre text-neutral-text caret-neutral-text outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                    )}
                  />
                </div>
                {/* Clear affordance: wipes text + tokens, keeps the depth scope. Never a
                    tab stop and guards focus so the field keeps the caret. */}
                {query.length > 0 && (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label="Clear search"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={clearQuery}
                    className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full text-neutral-alpha-7 hover:bg-neutral-2 hover:text-neutral-alpha-9"
                  >
                    <RiCloseLine className="h-[15px] w-[15px]" aria-hidden />
                  </button>
                )}
                <span className="shrink-0 whitespace-nowrap text-12 text-neutral-alpha-7">
                  {scoped ? (
                    `${scopedFoundCount} in ${scopedConfigLabel}`
                  ) : trimmed.length === 0 ? (
                    <kbd className="rounded-[6px] border border-neutral-5 bg-neutral-2 px-[7px] py-[2px] text-neutral-alpha-8">
                      esc
                    </kbd>
                  ) : loading ? (
                    'Searching…'
                  ) : (
                    `${totalFound} results`
                  )}
                </span>
              </motion.div>
            )}

            {/* Body — a sibling BELOW the bar (never its ancestor), so its slide/fade
                  can't move the bar's morph target. The enter delay lets content settle
                  just AFTER the panel rather than popping in with it. */}
            <div
              className={cn(
                'flex min-h-0 flex-1 flex-col',
                isExiting ? 'animate-gs-panel-out' : 'animate-gs-panel-in',
                'motion-reduce:animate-none'
              )}
            >
              {/* ── Chip row (type facets when unscoped, status sub-facets when
                    scoped) — ONE unified treatment, one Tab-driven cursor,
                    horizontal-only scroll with scroll-into-view and hover scroll
                    affordances. */}
              {chipRowVisible && (
                <ChipScrollRow
                  leadingLabel={scoped ? 'Status' : undefined}
                  cursorElementId={facetIndex !== null ? facetOptionId(facetIndex) : null}
                >
                  {cursorChips.map((chip, index) => (
                    <Chip
                      key={chip.key}
                      id={facetOptionId(index)}
                      label={chip.label}
                      count={chip.count}
                      selected={chip.selected}
                      cursored={facetIndex === index}
                      onSelect={chip.onSelect}
                    />
                  ))}
                </ChipScrollRow>
              )}

              {/* ── Body ── */}
              <div
                ref={listboxRef}
                id={LISTBOX_ID}
                role="listbox"
                aria-label="Search results"
                className="flex-1 overflow-y-auto px-[8px] pb-[8px] pt-[6px]"
              >
                {unavailable ? (
                  <Notice>Search is temporarily unavailable. Please try again later.</Notice>
                ) : scoped ? (
                  hasResults ? (
                    (scopedGroup?.items ?? []).map((item) => {
                      const index = nextActive();
                      return (
                        <ResultCard
                          key={item.id}
                          id={rowOptionId(index)}
                          data={item}
                          active={index === activeIndex}
                          onSelect={() => activate({ kind: 'result', item })}
                        />
                      );
                    })
                  ) : showSkeleton ? (
                    // Cold start only (see `showSkeleton`): a refine that empties the
                    // scoped list keeps the zero state rather than re-flashing here.
                    <Skeleton />
                  ) : trimmed.length === 0 ? (
                    // Scoped but no term yet (`lot:`): prompt instead of "No results for ''".
                    <Notice>Type to search {scopedConfigLabel.toLowerCase()}.</Notice>
                  ) : (
                    <ZeroResults query={trimmed} scopeLabel={scopedConfigLabel} />
                  )
                ) : trimmed.length === 0 ? (
                  // Empty query: recents first, then the quick create-actions. When
                  // there are no recents the actions stand alone (no empty "Recent"
                  // header, no stray separator); when neither exists, the prompt Notice.
                  recents.length === 0 && commands.length === 0 ? (
                    <Notice>
                      Search orders, lots, customers, invoices and more. Recently-opened records appear here.
                    </Notice>
                  ) : (
                    <>
                      {recents.length > 0 && (
                        <>
                          <SectionHeader title="Recent" aside="Today" />
                          {recents.map((item) => {
                            const index = nextActive();
                            return (
                              <ResultCard
                                key={item.route}
                                id={rowOptionId(index)}
                                data={item}
                                active={index === activeIndex}
                                onSelect={() => activate({ kind: 'result', item })}
                              />
                            );
                          })}
                        </>
                      )}
                      {commands.length > 0 && (
                        <>
                          <SectionHeader title="Actions" />
                          {commands.map((action) => {
                            const Icon = action.icon;
                            const index = nextActive();
                            return (
                              <ActionRow
                                key={action.id}
                                id={rowOptionId(index)}
                                action={action}
                                active={index === activeIndex}
                                onSelect={() => activate({ kind: 'command', action })}
                                icon={<Icon className="h-[17px] w-[17px] text-neutral-9" aria-hidden />}
                              />
                            );
                          })}
                        </>
                      )}
                    </>
                  )
                ) : (
                  <>
                    {/* Commands are local — always shown; only the search area below
                    swaps to the skeleton / zero-results states. */}
                    {commands.length > 0 && (
                      <>
                        <SectionHeader title="Actions" />
                        {commands.map((action) => {
                          const Icon = action.icon;
                          const index = nextActive();
                          return (
                            <ActionRow
                              key={action.id}
                              id={rowOptionId(index)}
                              action={action}
                              active={index === activeIndex}
                              onSelect={() => activate({ kind: 'command', action })}
                              icon={<Icon className="h-[17px] w-[17px] text-neutral-9" aria-hidden />}
                            />
                          );
                        })}
                      </>
                    )}
                    {showSkeleton ? (
                      <Skeleton />
                    ) : groups.length === 0 ? (
                      commands.length === 0 && <ZeroResults query={trimmed} />
                    ) : (
                      <>
                        {bestMatch &&
                          (() => {
                            const index = nextActive();
                            return (
                              <>
                                <div className="flex items-center gap-[6px] px-[10px] pb-[6px] pt-[12px]">
                                  <RiSparkling2Line className="h-[13px] w-[13px] text-brand-6" aria-hidden />
                                  <span className="text-11 font-semibold uppercase tracking-wider text-brand-6">
                                    Best match
                                  </span>
                                </div>
                                <ResultCard
                                  id={rowOptionId(index)}
                                  data={bestMatch}
                                  active={index === activeIndex}
                                  onSelect={() => activate({ kind: 'result', item: bestMatch })}
                                  emphasis
                                />
                              </>
                            );
                          })()}
                        {renderGroups.map((group) => (
                          <Group
                            key={group.key}
                            group={group}
                            onShowAll={() => enterScope(group.key)}
                            renderRow={(item) => {
                              const index = nextActive();
                              return (
                                <ResultCard
                                  key={item.id}
                                  id={rowOptionId(index)}
                                  data={item}
                                  active={index === activeIndex}
                                  onSelect={() => activate({ kind: 'result', item })}
                                />
                              );
                            }}
                          />
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>

              <Footer mode={footerMode} scopeLabel={scoped ? scopedConfigLabel : null} examples={scopeHints} />
            </div>
          </Dialog>
        </Modal>
      )}
    </ModalOverlay>
  );
}

function Chip({
  label,
  count,
  selected,
  cursored = false,
  onSelect,
  id,
}: {
  label: string;
  count: number;
  selected: boolean;
  cursored?: boolean;
  onSelect: () => void;
  id: string;
}) {
  return (
    <button
      type="button"
      // Virtual-cursor: chips are never a tab stop (focus stays on the field).
      // Pointer-down guards focus, onClick activates (so keyboard runs through the field
      // handler, not the chip).
      id={id}
      tabIndex={-1}
      role="option"
      aria-selected={selected}
      onPointerDown={(event) => event.preventDefault()}
      onClick={onSelect}
      className={cn(
        'flex h-[28px] shrink-0 items-center gap-[6px] whitespace-nowrap rounded-full border px-[11px] text-13 font-medium transition-colors',
        selected
          ? 'border-transparent bg-brand-alpha-2 text-brand-6'
          : 'border-neutral-5 bg-white text-neutral-alpha-9 hover:bg-neutral-2',
        cursored && 'ring-2 ring-brand-focus'
      )}
    >
      {label}
      <span className={cn('text-11', MONO, selected ? 'text-brand-6' : 'text-neutral-alpha-7')}>{count}</span>
    </button>
  );
}

/** Horizontally-scrolling chip row with hover scroll affordances. */
function ChipScrollRow({
  leadingLabel,
  cursorElementId,
  children,
}: {
  leadingLabel?: string;
  /** Element id of the cursored chip to keep in view, or null when the list has focus. */
  cursorElementId: string | null;
  children: React.ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  // Key the observer effect on chip COUNT, not the `children` array (a fresh reference
  // every parent render), so an arrow key or a settling request never tears down and
  // rebuilds the ResizeObserver.
  const chipCount = Children.count(children);

  // LEFT shows once scrolled off 0; RIGHT shows while short of the far edge. The 1px
  // slack absorbs sub-pixel rounding so a fully-scrolled row hides its end button.
  // Bail out when nothing changed so a no-op recompute never triggers a re-render.
  const recompute = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const next = { left: el.scrollLeft > 1, right: el.scrollLeft < maxScroll - 1 };
    setOverflow((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, []);

  // Recompute on mount, on container resize, and whenever the chip COUNT changes — the
  // results changing alters chip counts, which changes the clipped state.
  useLayoutEffect(() => {
    recompute();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => recompute());
    observer.observe(el);
    return () => observer.disconnect();
  }, [recompute, chipCount]);

  useEffect(() => {
    if (!cursorElementId) return;
    document
      .getElementById(cursorElementId)
      // block:'nearest' keeps the panel from scrolling vertically underneath.
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [cursorElementId]);

  const scrollByViewport = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  return (
    <div
      className="relative flex items-center border-b border-neutral-4"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* The leading label sits OUTSIDE the scroll container: a `listbox` may own only
          its `option`/`group` children, never a bare label. */}
      {leadingLabel && (
        <span className="flex h-[28px] shrink-0 items-center pl-[16px] pr-[2px] text-11 font-semibold uppercase tracking-wider text-neutral-alpha-7">
          {leadingLabel}
        </span>
      )}
      <div
        ref={scrollRef}
        role="listbox"
        aria-label={leadingLabel ? 'Filter by status' : 'Filter by type'}
        onScroll={recompute}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-[8px] overflow-x-auto overflow-y-hidden [overscroll-behavior:contain] py-[11px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          leadingLabel ? 'pr-[16px]' : 'px-[16px]'
        )}
      >
        {children}
      </div>
      {hovered && overflow.left && <ScrollButton side="left" onClick={() => scrollByViewport(-1)} />}
      {hovered && overflow.right && <ScrollButton side="right" onClick={() => scrollByViewport(1)} />}
    </div>
  );
}

/** Edge scroll affordance — a mouse-only overlay (kept out of the tab order; keyboard
 * users have Tab + scroll-into-view). Absolutely positioned so it never reflows the row. */
function ScrollButton({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? RiArrowLeftSLine : RiArrowRightSLine;
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden
      onPointerDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'absolute inset-y-0 z-10 flex w-[40px] items-center',
        side === 'left'
          ? 'left-0 justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-[6px]'
          : 'right-0 justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-[6px]'
      )}
    >
      <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full border border-neutral-5 bg-white shadow-[0_1px_3px_rgba(16,22,34,0.18)]">
        <Icon className="h-[15px] w-[15px] text-neutral-alpha-9" aria-hidden />
      </span>
    </button>
  );
}

function SectionHeader({ title, aside }: { title: string; aside?: string }) {
  return (
    <div className="flex items-center justify-between px-[10px] pb-[6px] pt-[12px]">
      <span className="text-11 font-semibold uppercase tracking-wider text-neutral-alpha-8">{title}</span>
      {aside && <span className="text-12 font-medium text-neutral-alpha-7">{aside}</span>}
    </div>
  );
}

function Group({
  group,
  onShowAll,
  renderRow,
}: {
  group: SearchResultGroup;
  /** Undefined when this group is already the active facet — the button would no-op. */
  onShowAll?: () => void;
  renderRow: (item: SearchResultItem) => React.ReactNode;
}) {
  return (
    <>
      <div className="flex items-center justify-between px-[10px] pb-[6px] pt-[14px]">
        <div className="flex items-center gap-[8px]">
          <span className="text-11 font-semibold uppercase tracking-wider text-neutral-alpha-8">
            {pluralize(group.section.label)}
          </span>
          <span className="text-11 font-medium text-neutral-alpha-7">{group.found}</span>
        </div>
        {onShowAll && group.found > group.items.length && (
          <button
            type="button"
            tabIndex={-1}
            onPointerDown={(event) => event.preventDefault()}
            onClick={onShowAll}
            className="flex items-center gap-[3px] text-12 font-medium text-brand-6"
          >
            Show all {group.found} →
          </button>
        )}
      </div>
      {group.items.map(renderRow)}
    </>
  );
}

function ActionRow({
  active,
  onSelect,
  icon,
  action,
  id,
}: {
  active: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  action: CommandAction;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onPointerDown={(event) => event.preventDefault()}
      onClick={onSelect}
      className={cn(
        'flex h-[52px] w-full items-center gap-[12px] rounded-[10px] px-[10px] text-left',
        active ? 'bg-brand-alpha-2' : 'hover:bg-neutral-alpha-2'
      )}
    >
      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-neutral-3">
        {icon}
      </span>
      <span className="truncate text-14 font-semibold text-neutral-text">{action.label}</span>
    </button>
  );
}

function Skeleton() {
  return (
    <div aria-hidden>
      <div className="px-[10px] pb-[6px] pt-[12px]">
        <Shimmer className="h-[11px] w-[110px]" />
      </div>
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex h-[52px] items-center gap-[12px] px-[10px]">
          <Shimmer className="h-[30px] w-[30px] shrink-0 rounded-[8px]" />
          <div className="flex-1">
            <Shimmer className="h-[12px] w-3/5" />
            <Shimmer className="mt-[8px] h-[10px] w-2/5" />
          </div>
          <Shimmer className="h-[20px] w-[64px] shrink-0 rounded-full" />
          <Shimmer className="h-[13px] w-[80px] shrink-0" />
        </div>
      ))}
    </div>
  );
}

function Shimmer({ className }: { className?: string }) {
  return <div className={cn('rounded-[4px] bg-neutral-3 animate-pulse', className)} />;
}

function ZeroResults({ query, scopeLabel = null }: { query: string; scopeLabel?: string | null }) {
  return (
    <div className="flex flex-col items-center px-[24px] pb-[60px] pt-[56px] text-center">
      <div className="flex h-[56px] w-[56px] items-center justify-center rounded-[15px] border border-neutral-4 bg-neutral-2">
        <RiSearch2Line className="h-[26px] w-[26px] text-neutral-alpha-7" aria-hidden />
      </div>
      <div className="mt-[16px] text-16 font-semibold text-neutral-text">No results for “{query}”</div>
      <div className="mt-[6px] max-w-[360px] text-14 text-neutral-alpha-8">
        Check your spelling, or try a different term.{' '}
        {scopeLabel
          ? `Searched ${scopeLabel} only — press Esc to search all record types.`
          : 'Searched all record types.'}
      </div>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return <div className="px-16 py-24 text-center text-14 text-neutral-alpha-8">{children}</div>;
}

type FooterMode = 'default' | 'scoped' | 'zero' | 'loading';

function Footer({
  mode,
  scopeLabel = null,
  examples = [],
}: {
  mode: FooterMode;
  scopeLabel?: string | null;
  examples?: string[];
}) {
  return (
    <div className="flex h-[40px] shrink-0 items-center gap-[16px] border-t border-neutral-4 bg-neutral-1 px-[16px] text-12 text-neutral-alpha-8">
      {mode === 'loading' ? (
        <>
          <RiLoader4Line className="h-[14px] w-[14px] animate-spin text-neutral-alpha-7" aria-hidden />
          <span>Searching {scopeLabel ?? 'all record types'}…</span>
        </>
      ) : mode === 'zero' ? (
        <>
          <RiLightbulbLine className="h-[14px] w-[14px] shrink-0 text-orange-text" aria-hidden />
          {/* One flowing sentence: the `<Kbd>` chips are inline-flex (see `Kbd`), so
              they sit inside the copy instead of block-breaking onto their own line. */}
          {examples.length > 0 ? (
            <span>
              Tip: narrow with a scope like{' '}
              {examples.map((example, index) => (
                <Fragment key={example}>
                  {index > 0 && ' or '}
                  <Kbd>{example}</Kbd>
                </Fragment>
              ))}{' '}
              before your search
            </span>
          ) : (
            <span>Tip: check your spelling, or try a shorter term</span>
          )}
        </>
      ) : mode === 'scoped' ? (
        <>
          <FooterHint keys={['↵']} label="open record" />
          <div className="flex-1" />
          <FooterHint keys={['esc']} label="back to all results" />
        </>
      ) : (
        <>
          <FooterHint keys={['↑', '↓']} label="navigate" />
          <span className="flex items-center gap-[6px] whitespace-nowrap">
            <Kbd>
              <RiCornerDownLeftLine className="h-3 w-3" aria-hidden />
            </Kbd>
            open
          </span>
          <FooterHint keys={['Tab']} label="cycle filters" />
        </>
      )}
    </div>
  );
}

function FooterHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-[6px] whitespace-nowrap">
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
      {label}
    </span>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  // GS_KBD_CLASS is the SAME visual the in-field chip renders (queryField.ts), so the tip `lot:` and the typed `so:1291` chip are one object. inline-flex (not flex) keeps this tip flowing inside the surrounding copy.
  return <kbd className={GS_KBD_CLASS}>{children}</kbd>;
}
